import clsx from 'clsx';

import { SectionProps } from './section-types';
import { getChildSpans } from '../helpers';
import { children, createMemo, createSignal, mergeProps, useContext, onMount, createUniqueId } from 'solid-js';
import { getBackgroundImageSrcData, ImageSrcData } from '../../components/image-component/image-component';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { focusPosition } from '../../tools/get-image-focus';
import { PageContext } from '../../components/page/page-context-provider';
import theme from '../../style/theme';

const { palette } = theme;
const DEFAULT_COLUMNS = 12;

const horizontalAlignmentClasses = {
    left: 'section-align-horizontal-left',
    center: 'section-align-horizontal-center',
    right: 'section-align-horizontal-right',
} as const;

const verticalAlignmentClasses = {
    top: 'section-align-vertical-top',
    center: 'section-align-vertical-center',
    bottom: 'section-align-vertical-bottom',
    equalHeightColumns: 'section-align-vertical-stretch',
    spread: 'section-align-vertical-spread',
} as const;

const defaultProps = {
    isLayoutSection: true,
    autoResponsiveness: true,
    removeGridGap: false,
    equalHeight: false,
};

const sumSpans = (values: number[]) => values.reduce((total, value) => total + value, 0);

/* What the spacing dropdown can write today. `spaceBelowSection` also holds dead
   2022 values ('small' | 'medium' | 'large' | 'xlarge') on 599 sections that are
   never rendered, so match the list rather than testing truthiness. */
const SECTION_SPACE_VALUES = ['tight', 'close', 'none', 'spacious'];

export const Section = (componentProps: SectionProps) => {
    const {
        imagesServiceUrl,
        supportedImageFormats,
        viewport,
    } = useContext(AppContext);

    const { withSidebarMenu } = useContext(PageContext);
    const [ backgroundImageSrcData, setBackgroundImageSrcData ] = createSignal<ImageSrcData | undefined>();

    const props = mergeProps(defaultProps, componentProps);
    const spans = createMemo(() =>
        getChildSpans({
            templateShorthand: props.templateShorthand,
            template: props.template
        })
    );

    const resolved = children(() => props.children);
    const childArray = createMemo(() => resolved.toArray());

    const imageWidth = () => {
        if (viewport.width <= theme.breakpoints.MOBILE) {
            return 480;
        }

        if (viewport.width <= theme.breakpoints.TABLET && viewport.width > theme.breakpoints.MOBILE) {
            return 960;
        }

        if (viewport.width <= theme.breakpoints.DESKTOP_LARGE && viewport.width > theme.breakpoints.TABLET) {
            return 1500;
        }

        return 1920;
    };

    onMount(() => {
        const src = props.backgroundValue?.url;

        if (props.backgroundType === 'image' && src ) {
            const backgroundImage: ImageSrcData = getBackgroundImageSrcData({
                imagesServiceUrl,
                supportedImageFormats,
                src,
                width: imageWidth() || 480,
                height: 'AUTO',
            });

            setBackgroundImageSrcData(backgroundImage);
        } else {
            setBackgroundImageSrcData(undefined);
        }
    });

    const layoutGrid = createMemo(() =>
        typeof props.isLayoutSection === 'undefined' ? defaultProps.isLayoutSection : props.isLayoutSection
    );

    const autoResponsiveness = createMemo(() =>
        typeof props.autoResponsiveness === 'undefined' ? defaultProps.autoResponsiveness : props.autoResponsiveness
    );

    const columns = createMemo(() => {
        const fromProps = props.templateShorthand?.length ? sumSpans(props.templateShorthand) : 0;
        const fromSpans = sumSpans(spans());
        const resolvedColumns = props.templateShorthand?.length ? fromProps : fromSpans;
        return resolvedColumns > 0 ? resolvedColumns : DEFAULT_COLUMNS;
    });

    const layoutTemplate = (cols: number) => {
        const columnSize = props.widthType === 'full'
            ? '1fr'
            : `${theme.grid.colSize()}rem`;
        const standardUnit = `${theme.grid.standardUnit}rem`;
        return [
            'auto',
            standardUnit,
            `repeat(${cols}, minmax(0, ${columnSize}))`,
            standardUnit,
            'auto',
        ].join(' ');
    };


    const layoutTabletTemplate = (cols: number) => [
        'auto',
        `repeat(${cols}, minmax(0, 1fr))`,
        'auto',
    ].join(' ');

    const standardTemplate = (cols: number) => `repeat(${cols}, minmax(0, 1fr))`;

    const templates = createMemo(() => {
        const cols = columns();
        const auto = autoResponsiveness();

        const tabletColumns = auto ? DEFAULT_COLUMNS : cols;
        const mobileColumns = auto ? DEFAULT_COLUMNS : cols;

        const desktop = layoutGrid() ? layoutTemplate(cols) : standardTemplate(cols);
        const tablet = layoutGrid()
            ? layoutTabletTemplate(tabletColumns)
            : standardTemplate(tabletColumns);

        const mobile = layoutGrid()
            ? layoutTabletTemplate(mobileColumns)
            : standardTemplate(mobileColumns);

        return {
            desktop,
            tablet,
            mobile,
        };
    });

    const rowGaps = createMemo(() => {
        const hasMultiple = spans().length > 1;
        return {
            desktop: hasMultiple ? 'var(--standardUnit)' : '0px',
            tablet: hasMultiple ? 'var(--standardUnit)' : '0px',
            mobile: 'var(--gapSizeTabletSmall)',
        };
    });

    const tabletSpans = createMemo(() => {
        if (!layoutGrid() && autoResponsiveness()) {
            return [ DEFAULT_COLUMNS ];
        }

        return spans();
    });

    const mobileSpans = createMemo(() => autoResponsiveness() ? [ DEFAULT_COLUMNS ] : spans());

    const hasBackgroundImage = createMemo(() => {
        const data = backgroundImageSrcData();
        return props.backgroundType === 'image' && !!data && data.original !== '';
    });

    const sectionFocus = createMemo(() => focusPosition(props?.backgroundValue?.focus));

    const sectionClasses = createMemo(() => clsx(
        'atos-section',
        'section-base',
        'section-spacing',
        'section-spacing-desktop',
        'section-spacing-tablet',
        props.heightType === 'fill' && !props.customHeight ? 'section-height-fill' : undefined,
        props.horizontalAlignment && horizontalAlignmentClasses[props.horizontalAlignment],
        props.verticalAlignment && verticalAlignmentClasses[props.verticalAlignment as keyof typeof verticalAlignmentClasses],
        {
            'section-padding-default': !props.customHeight && props.heightType !== 'fill',
            // Per-section spacing overrides. The attributes have lived in the
            // Set from the section's own attributes, by the migration on members of
            // a repeating series and by editors via the row inspector.
            'section-space-above-close': props.spaceAboveSection === 'close',
            'section-space-below-close': props.spaceBelowSection === 'close',
            'section-space-above-tight': props.spaceAboveSection === 'tight',
            'section-space-below-tight': props.spaceBelowSection === 'tight',
            'section-space-above-spacious': props.spaceAboveSection === 'spacious',
            'section-space-below-spacious': props.spaceBelowSection === 'spacious',
            // Explicit "Default" (`'none'`) restores the 80px rebrand rhythm; leaving
            // a section NOT SET (undefined) keeps the relaxed `.section-padding-default`.
            'section-space-above-default': props.spaceAboveSection === 'none',
            'section-space-below-default': props.spaceBelowSection === 'none',
            'section-block-spacing': !!props.customHeight && !props.removeSpacingBlock,
            'section-equal-height': props.equalHeight,
            'section-sidebar-offset': withSidebarMenu(),
            /* Legacy `combineWithBelow` still glues the pair, but an explicit
               `spaceBelowSection` wins: the legacy rule is `!important`, so
               without this gate the dropdown saved and did nothing. */
            'section-combine-below': props.combineWithBelowSection
                && !SECTION_SPACE_VALUES.includes(props.spaceBelowSection as string),
            'section-overlay-on': props.overlayType === 'on' && hasBackgroundImage(),
            'section-overlay-auto': props.overlayType === 'auto' && hasBackgroundImage(),
            // The value must be there too, not just the type. Editors leave behind
            // `backgroundType: 'color'` with a null value — a dead hook, five of
            // them on one audited page. The inline style below already checks both
            // and paints transparent, but this class did not, so on sidebar pages
            // those sections still took the rebrand's tinted-card treatment: 40px
            // of padding and an 8px radius around no colour at all. The visible
            // result is a section indented 40px from its neighbours for no reason.
            /* Only when the section paints something visible. This class adds a
               40px margin and a horizontal inset, wrong for a colour that draws
               nothing: 'white' is invisible on a white page, and a raw hex is not
               a palette key so no background-color is emitted at all. */
            'section-with-color': props.backgroundType === 'color'
                && !!props.backgroundValue
                && props.backgroundValue !== 'white'
                && !!palette[props.backgroundValue as keyof typeof palette],
            // Wide-gutter hook: tailwind.css gives these ds-space(11). Also used
            // by the zig-zag spacing rules.
            'section-even-split': spans().length === 2 && spans()[0] === spans()[1],
        }
    ));

    const sectionInlineStyle = createMemo(() => {
        const declarations: string[] = [];

        const height = props.customHeight
            ? props.customHeight
            : props.heightType === 'fill'
                ? '100%'
                : undefined;

        if (height) {
            declarations.push(`height: ${height}`);
        }

        const backgroundColour = props.backgroundType === 'color' && props.backgroundValue
            ? palette[props.backgroundValue as keyof typeof palette]
            : 'transparent';
        declarations.push(`background-color: ${backgroundColour}`);

        if (hasBackgroundImage()) {
            const original = backgroundImageSrcData()!.original;
            declarations.push(`background-image: url(${original})`);
            declarations.push('background-repeat: no-repeat');
            declarations.push('background-size: cover');
            declarations.push(`background-position: ${sectionFocus() || 'center'}`);
        }

        const templateValues = templates();
        declarations.push(`--grid-template-desktop: ${templateValues.desktop}`);
        declarations.push(`--grid-template-tablet: ${templateValues.tablet}`);
        declarations.push(`--grid-template-mobile: ${templateValues.mobile}`);

        declarations.push('--grid-column-gap-desktop: var(--gapSize)');
        declarations.push('--grid-column-gap-tablet: var(--gapSizeMobile)');
        declarations.push('--grid-column-gap-mobile: var(--grid-mobile-column-gap)');

        const gapValues = rowGaps();
        declarations.push(`--grid-row-gap-desktop: ${gapValues.desktop}`);
        declarations.push(`--grid-row-gap-tablet: ${gapValues.tablet}`);
        declarations.push(`--grid-row-gap-mobile: ${gapValues.mobile}`);

        const customCss = props.customCss?.trim();
        if (customCss) {
            declarations.push(customCss);
        }

        return declarations.join('; ');
    });

    const generatedId = createUniqueId();
    const sectionUniqueId = createMemo(() => {
        return props.sectionIdentifier || `section-${generatedId}`;
    });

    const dynamicChildSpansCss = createMemo(() => {
        const desktopSpans = spans();
        const tabletValues = tabletSpans();
        const mobileValues = mobileSpans();
        const childCount = childArray().length;

        if (childCount === 0) return '';

        const rules: string[] = [];
        const sectionId = sectionUniqueId();

        for (let i = 0; i < childCount; i++) {
            const desktopSpan = desktopSpans[i % desktopSpans.length] ?? DEFAULT_COLUMNS;
            const tabletSpan = tabletValues[i % tabletValues.length] ?? desktopSpan;
            const mobileSpan = mobileValues[i % mobileValues.length] ?? tabletSpan;

            rules.push(`
                #${sectionId} > *:nth-child(${i + 1}) {
                    --grid-item-span-desktop: ${desktopSpan};
                    --grid-item-span-tablet: ${tabletSpan};
                    --grid-item-span-mobile: ${mobileSpan};
                }
            `);
        }

        return rules.join('\n');
    });

    return (
        <ErrorCatcher componentName='Section'>
            <div
                id={sectionUniqueId()}
                class={sectionClasses()}
                style={sectionInlineStyle()}
                data-layout-grid={layoutGrid() ? 'true' : 'false'}
                data-auto-responsiveness={autoResponsiveness() ? 'true' : 'false'}
                data-child-count={childArray().length}
            >
                {resolved()}
            </div>
            <style>{dynamicChildSpansCss()}</style>
        </ErrorCatcher>
    );

};

Section.parseProps = (atts: any) => {
    return {
        backgroundType: atts.backgroundType,
        backgroundValue: atts.backgroundValue,
        template: atts.template,
        widthType: atts.widthTypeFull ? 'full' : 'bgFull',
        colorSelection: atts.colorSelection,
        equalHeight: atts.equalHeight,
        horizontalAlignment: atts.horizontalAlignment,
        verticalAlignment: atts.verticalAlignment,
        roundCorners: atts.roundCorners,
        sectionIdentifier: atts.sectionIdentifier,
        isLayoutSection: atts.isLayoutSection,
        autoResponsiveness: atts.autoResponsiveness,
        combineWithBelowSection: atts.combineWithBelow,
        spaceAboveSection: atts.spaceAboveSection,
        spaceBelowSection: atts.spaceBelowSection,
        overlayType: atts.overlayType,
    };
};
